import { useState, useEffect, useCallback, useMemo } from "preact/hooks";
import {
  getClassProperties,
  resolveColorToHex,
  type ClassOverrides,
} from "./types";

type InspectTabProps = {
  active: boolean;
  classOverrides: ClassOverrides;
  onSetClassProperty: (cls: string, prop: string, value: string) => void;
  onResetClassProperty: (cls: string, prop: string) => void;
};

type ClassData = {
  name: string;
  properties: Record<string, string>;
};

function isColorProp(prop: string): boolean {
  return (
    prop === "color" ||
    prop === "background-color" ||
    prop === "background" ||
    prop === "border-color" ||
    prop.endsWith("-color")
  );
}

export function InspectTab({
  active,
  classOverrides,
  onSetClassProperty,
  onResetClassProperty,
}: InspectTabProps) {
  const [hovered, setHovered] = useState<HTMLElement | null>(null);
  const [pinnedClasses, setPinnedClasses] = useState<ClassData[]>([]);
  const [pinnedEl, setPinnedEl] = useState<HTMLElement | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const dismiss = useCallback(() => {
    if (pinnedEl) pinnedEl.style.outline = "";
    setPinnedClasses([]);
    setPinnedEl(null);
  }, [pinnedEl]);

  // Hover outline (only when nothing is pinned)
  useEffect(() => {
    if (!active) {
      if (hovered) {
        hovered.style.outline = "";
        setHovered(null);
      }
      dismiss();
      return;
    }
    if (pinnedEl) return;

    const handleOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target || target.closest(".festapp-customizer")) return;

      const has = Array.from(target.classList).some((c) =>
        c.startsWith("festapp-")
      );

      if (!has) {
        if (hovered) {
          hovered.style.outline = "";
          setHovered(null);
        }
        return;
      }

      if (hovered && hovered !== target) hovered.style.outline = "";
      target.style.outline = "2px dashed var(--festapp-primary, #F14E38)";
      setHovered(target);
    };

    const handleOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target && target === hovered) {
        target.style.outline = "";
        setHovered(null);
      }
    };

    document.addEventListener("mouseover", handleOver, true);
    document.addEventListener("mouseout", handleOut, true);
    return () => {
      document.removeEventListener("mouseover", handleOver, true);
      document.removeEventListener("mouseout", handleOut, true);
      if (hovered) hovered.style.outline = "";
    };
  }, [active, hovered, pinnedEl, dismiss]);

  // Click-to-pin with property reading
  useEffect(() => {
    if (!active) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target || target.closest(".festapp-customizer")) return;

      const classes = Array.from(target.classList).filter((c) =>
        c.startsWith("festapp-")
      );

      if (!classes.length) {
        dismiss();
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (pinnedEl) pinnedEl.style.outline = "";
      if (hovered) hovered.style.outline = "";
      setHovered(null);

      target.style.outline = "2px solid var(--festapp-primary, #F14E38)";
      setPinnedEl(target);

      // Read CSS properties for each class
      const data: ClassData[] = classes.map((name) => ({
        name,
        properties: getClassProperties(name),
      }));
      setPinnedClasses(data);
      setCollapsed({});
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [active, pinnedEl, hovered, dismiss]);

  // Recap: all class overrides across all elements
  const recapEntries = useMemo(() => {
    const entries: { cls: string; prop: string; value: string }[] = [];
    for (const [cls, props] of Object.entries(classOverrides)) {
      for (const [prop, value] of Object.entries(props)) {
        entries.push({ cls, prop, value });
      }
    }
    return entries;
  }, [classOverrides]);

  if (!active) return null;

  const toggleClass = (name: string) => {
    setCollapsed((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div className="festapp-customizer-inspect-tab">
      {/* Pinned element editor */}
      {pinnedClasses.length > 0 ? (
        <div className="festapp-customizer-inspect-editor">
          <div className="festapp-customizer-inspect-header">
            <span>Selected Element</span>
            <button
              type="button"
              className="festapp-customizer-inspect-dismiss"
              onClick={dismiss}
            >
              &times;
            </button>
          </div>

          {pinnedClasses.map(({ name, properties }) => {
            const overrides = classOverrides[name] || {};
            const allProps = { ...properties, ...overrides };
            const isCollapsed = collapsed[name];

            return (
              <div key={name} className="festapp-customizer-inspect-class-block">
                <button
                  type="button"
                  className="festapp-customizer-inspect-class-title"
                  onClick={() => toggleClass(name)}
                >
                  <svg
                    className={`festapp-customizer-chevron ${isCollapsed ? "" : "festapp-customizer-chevron-open"}`}
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                  <span className="festapp-customizer-inspect-selector">
                    .{name}
                  </span>
                  {Object.keys(overrides).length > 0 && (
                    <span className="festapp-customizer-inspect-badge">
                      {Object.keys(overrides).length}
                    </span>
                  )}
                </button>

                {!isCollapsed && (
                  <div className="festapp-customizer-inspect-props">
                    {Object.entries(allProps).map(([prop, value]) => {
                      const isOverridden = prop in overrides;
                      const currentValue = isOverridden
                        ? overrides[prop]
                        : value;
                      const showColorPicker = isColorProp(prop);

                      return (
                        <div
                          key={prop}
                          className={`festapp-customizer-inspect-prop ${isOverridden ? "festapp-customizer-inspect-prop-modified" : ""}`}
                        >
                          <span className="festapp-customizer-inspect-prop-name">
                            {prop}
                          </span>
                          <div className="festapp-customizer-inspect-prop-inputs">
                            {showColorPicker && (
                              <input
                                type="color"
                                className="festapp-customizer-color-picker festapp-customizer-color-picker-sm"
                                value={resolveColorToHex(currentValue)}
                                onInput={(e) =>
                                  onSetClassProperty(
                                    name,
                                    prop,
                                    (e.target as HTMLInputElement).value
                                  )
                                }
                              />
                            )}
                            <input
                              type="text"
                              className="festapp-customizer-text-input"
                              value={currentValue}
                              onInput={(e) =>
                                onSetClassProperty(
                                  name,
                                  prop,
                                  (e.target as HTMLInputElement).value
                                )
                              }
                            />
                            {isOverridden && (
                              <button
                                type="button"
                                className="festapp-customizer-reset-btn"
                                onClick={() =>
                                  onResetClassProperty(name, prop)
                                }
                                title="Reset to default"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="10"
                                  height="10"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                  <path d="M3 3v5h5" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="festapp-customizer-inspect-empty">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="festapp-customizer-inspect-empty-icon"
          >
            <path d="M15 15l-2 5L9 9l11 4-5 2z" />
            <path d="M2 2l8 8" />
          </svg>
          <span className="festapp-customizer-inspect-empty-text">
            Click any element in the widget to start editing
          </span>
          <span className="festapp-customizer-inspect-empty-sub">
            Hover to highlight, click to select
          </span>
        </div>
      )}

      {/* Recap of all edits */}
      {recapEntries.length > 0 && (
        <div className="festapp-customizer-inspect-recap">
          <div className="festapp-customizer-inspect-recap-title">
            All Edits ({recapEntries.length})
          </div>
          {recapEntries.map(({ cls, prop, value }) => (
            <div
              key={`${cls}:${prop}`}
              className="festapp-customizer-inspect-recap-item"
            >
              <span className="festapp-customizer-inspect-recap-selector">
                .{cls}
              </span>
              <span className="festapp-customizer-inspect-recap-prop">
                {prop}: {value}
              </span>
              <button
                type="button"
                className="festapp-customizer-reset-btn"
                onClick={() => onResetClassProperty(cls, prop)}
                title="Remove override"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
